import { UseFormReturn, useWatch } from 'react-hook-form';
import { IDocumentCalc, ISetValue } from '@models/Forms/IForms';
import FieldWrapper from '../FieldWrapper/FieldWrapper';
import './Calc.scss';
import { IFieldElem } from '@models/IFormData';
import { FormulaManager } from '@utils/managers/FormulaManager';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { handlerFieldWatch } from '@utils/fieldWatchUtils';
import { ChangesFieldValue, GetValueForSetValue } from '@utils/managers/ChangesManager';
import DisplayField from '@atoms/DisplayField/DisplayField';
import { ValueType } from '@/types/ValueType';
import ToolTipTitle from '@atoms/ToolTipTitle/ToolTipTitle';
import { IPartialFormGroup } from '@controls/types';
import { round } from 'mathjs';
import InputNumberControl from '@controls/InputNumberControl';

export interface ICalcProps<TFieldValues extends object = object> {
    calc?: IDocumentCalc;
    formMethods: UseFormReturn<TFieldValues>;
    setError: (errors?: string[]) => void;
    isEdit: boolean;
    isNew: boolean;
    fields: Record<string, IFieldElem>;
    includedFields: number[];
}

const Calc = <TFieldValues extends object = object>({
    calc,
    formMethods,
    setError,
    isEdit,
    isNew,
    fields,
    includedFields,
    ...props
}: ICalcProps<TFieldValues>) => {
    const visibilityRules = calc?.visibilityRules;
    const readOnlyRules = calc?.readonly;
    const requiredRules = calc?.required;
    const formulaRules = calc?.formula;

    const visibilityMng = useMemo(() => {
        const manager = new FormulaManager(visibilityRules!);
        manager.Init(fields, formMethods);
        return manager;
    }, [visibilityRules, fields, formMethods]);

    const readOnlyMng = useMemo(() => {
        const manager = new FormulaManager(readOnlyRules!);
        manager.Init(fields, formMethods);
        return manager;
    }, [readOnlyRules, fields, formMethods]);

    const requiredMng = useMemo(() => {
        const manager = new FormulaManager(requiredRules!);
        manager.Init(fields, formMethods);
        return manager;
    }, [requiredRules, fields, formMethods]);

    const formulaMng = useMemo(() => {
        const manager = new FormulaManager(formulaRules!);
        manager.Init(fields, formMethods);
        return manager;
    }, [formulaRules, fields, formMethods]);

    const activated = React.useRef(false);
    const typeValue = calc?.valueType !== undefined ? calc?.valueType : ValueType.Integer;
    const [visibility, setVisibility] = useState<boolean>(false);
    const [required, setRequired] = useState<boolean>(false);
    const [readOnly, setReadOnly] = useState<boolean>(false);

    const InitFormulas = async () => {
        let vis = await visibilityMng.EvalFormulaValues(isEdit, isNew);
        if (activated.current) {
            setVisibility(vis);
        }

        if (requiredRules) {
            let req = await requiredMng.EvalFormulaValues(isEdit, isNew);
            if (activated.current) {
                setRequired(req);
            }
        }

        if (readOnlyRules) {
            let readonly = await readOnlyMng.EvalFormulaValues(isEdit, isNew);
            if (activated.current) {
                setReadOnly(readonly);
            }
        }
    };

    useEffect(() => {
        activated.current = true;
        InitFormulas();
        return () => {
            activated.current = false;
        };
    }, []);

    const watchVisibility = useWatch({
        name: visibilityMng.GetWatchFields(),
    });
    const watchReadOnly = useWatch({
        name: readOnlyMng.GetWatchFields(),
    });
    const watchRequired = useWatch({
        name: requiredMng.GetWatchFields(),
    });
    const watchFormula = useWatch({
        name: formulaMng.GetWatchFields(),
    });

    useEffect(() => {
        handlerFieldWatch(watchVisibility, isEdit, isNew, visibilityMng, setVisibility, activated);
    }, [watchVisibility, isEdit, isNew]);
    useEffect(() => {
        if (readOnlyRules) {
            handlerFieldWatch(watchReadOnly, isEdit, isNew, readOnlyMng, setReadOnly, activated);
        }
    }, [watchReadOnly, isEdit, isNew]);
    useEffect(() => {
        if (requiredRules) {
            handlerFieldWatch(watchRequired, isEdit, isNew, requiredMng, setRequired, activated);
        }
    }, [watchRequired, isEdit, isNew]);

    const dataField = fields[calc?.key!];
    const idField = dataField?.name;
    includedFields.push(dataField?.index);

    const calcFormula = useCallback(
        async (watchFormula: any[], isEdit: boolean, isNew: boolean) => {
            let val = await formulaMng.EvalFormulaValues(isEdit, isNew, watchFormula);

            if (typeValue == ValueType.Text) {
                formMethods.setValue(idField as any, val, { shouldDirty: true });
                return;
            }

            let valForm = +val;
            if (calc?.floatPoints) {
                valForm = round(valForm, calc?.floatPoints);
            }
            formMethods.setValue(idField as any, valForm as any, { shouldDirty: true });
        },
        [calc?.floatPoints, formMethods, formulaMng, idField, typeValue],
    );

    useEffect(() => {
        if (watchFormula.length != 0 && visibility) {
            if (!watchFormula.every((element) => element === undefined)) {
                calcFormula(watchFormula, isEdit, isNew);
            }
        }
    }, [watchFormula, isEdit, isNew]);

    const onSaveField = useCallback(
        async (item: ISetValue, rowData?: any) => {
            let val = await GetValueForSetValue(item, undefined, fields, rowData, formMethods);
            let field = fields[item.key];
            if (field === undefined) throw new Error(`Поле ${item.key} не найдено в перечне полей`);
            formMethods.setValue(field.name as any, val, { shouldDirty: true });
        },
        [fields, formMethods],
    );

    const onInputNumberChange = useCallback(
        async (value: number) => {
            await ChangesFieldValue(
                calc?.changes!,
                value,
                calc?.key!,
                fields,
                isEdit,
                isNew,
                onSaveField,
                setError,
                undefined,
                undefined,
                formMethods,
            );
        },
        [calc?.changes, calc?.key, fields, formMethods, isEdit, isNew, onSaveField, setError],
    );

    useEffect(() => {
        if (
            !required &&
            formMethods.formState.errors &&
            (formMethods.formState.errors as any).fields &&
            (formMethods.formState.errors as any).fields[dataField?.index]
        ) {
            formMethods.clearErrors(dataField?.name as any);
        }
    }, [required]);

    const fieldControlType = () => {
        switch (typeValue) {
            case ValueType.Double:
                return (
                    <InputNumberControl
                        label={calc?.name}
                        tooltip={<ToolTipTitle title={calc?.title} />}
                        formGroupProps={{ verticalBottomAlign: calc?.verticalBottomAlign } as IPartialFormGroup}
                        groupBy={0}
                        separator={''}
                        readOnly={readOnly}
                        required={required}
                        floatPoints={calc?.floatPoints ?? 2}
                        type="number"
                        step={0.01}
                        rules={{
                            required: {
                                value: required ? required : false,
                                message: 'Обязательное поле',
                            },
                        }}
                        name={idField}
                        formState={formMethods.formState}
                        control={formMethods.control}
                        onValueChange={onInputNumberChange}
                        placeholder={calc?.placeholder ? calc.placeholder : undefined}
                        testid={calc?.key}
                    />
                );
            case ValueType.Money:
                return (
                    <InputNumberControl
                        label={calc?.name}
                        tooltip={<ToolTipTitle title={calc?.title} />}
                        formGroupProps={{ verticalBottomAlign: calc?.verticalBottomAlign } as IPartialFormGroup}
                        readOnly={readOnly}
                        required={required}
                        floatPoints={calc?.floatPoints ?? 2}
                        step={0.01}
                        rules={{
                            required: {
                                value: required ? required : false,
                                message: 'Обязательное поле',
                            },
                        }}
                        name={idField}
                        formState={formMethods.formState}
                        control={formMethods.control}
                        onValueChange={onInputNumberChange}
                        placeholder={calc?.placeholder ? calc.placeholder : undefined}
                        testid={calc?.key}
                    />
                );

            default:
                return (
                    <InputNumberControl
                        label={calc?.name}
                        tooltip={<ToolTipTitle title={calc?.title} />}
                        formGroupProps={{ verticalBottomAlign: calc?.verticalBottomAlign } as IPartialFormGroup}
                        groupBy={0}
                        separator={''}
                        rules={{
                            required: {
                                value: required ? required : false,
                                message: 'Обязательное поле',
                            },
                        }}
                        readOnly={readOnly}
                        required={required}
                        name={idField}
                        type="number"
                        formState={formMethods.formState}
                        control={formMethods.control}
                        onValueChange={onInputNumberChange}
                        placeholder={calc?.placeholder ? calc.placeholder : undefined}
                        testid={calc?.key}
                    />
                );
        }
    };

    return calc && calc.key && visibility && idField != undefined ? (
        <div className="form-field" data-testid={calc.id ? `calc-${calc.id}` : undefined}>
            {isEdit || isNew ? (
                fieldControlType()
            ) : (
                <FieldWrapper
                    inLineFormat={calc.isValue}
                    label={calc.name}
                    title={calc.viewTitle}
                    testid={calc.key}
                    verticalBottomAlign={calc?.verticalBottomAlign}
                >
                    <DisplayField type={typeValue} label={calc.name} value={dataField.value} />
                </FieldWrapper>
            )}
        </div>
    ) : (
        <></>
    );
};

export default Calc;
